import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { exploreAPI, partnerAPI } from '../services/api';

/**
 * /play/:starterId — one-URL entry into a live guided session.
 *
 * This is the Scan-to-Play QR target. Three ways in, all ending in the session:
 * 1. Existing session (logged-in user or returning guest) — plays directly.
 * 2. Pre-issued token in the URL (?token=...&oauth=success) — magic-link demo
 *    flow; AuthContext hydrates it on startup before this page acts.
 * 3. Nothing at all (the real QR scan) — a guest identity is minted silently
 *    and the visitor lands in their own session. No app, no account.
 */
const PlayPage = () => {
  const { starterId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const de = (i18n?.language || '').toLowerCase().startsWith('de');
  const { isAuthenticated, isLoading, user, startGuestSession } = useAuth();
  const [error, setError] = useState(null);
  // Venue over its monthly session budget (blocked band) — a calm, branded
  // pause screen instead of an error; never mentions budgets or cost.
  const [venueBusy, setVenueBusy] = useState(false);
  const [busyPartner, setBusyPartner] = useState(null);
  // Ambiguous re-entry: an explicitly ended run with partial progress exists.
  // The backend won't guess — it returns counts and we ask (continue / fresh).
  const [resumeChoice, setResumeChoice] = useState(null);
  const [choiceBusy, setChoiceBusy] = useState(false);
  const startedRef = useRef(false);
  const guestAttemptedRef = useRef(false);
  const rescopeAttemptedRef = useRef(false);
  const startFnRef = useRef(null);

  const showVenueBusy = useCallback(async () => {
    setVenueBusy(true);
    try {
      const response = await partnerAPI.getByStarter(starterId);
      setBusyPartner(response.data?.data?.partner || null);
    } catch (_) {
      // Screen works without the venue link
    }
  }, [starterId]);

  useEffect(() => {
    if (isLoading || startedRef.current) return;

    if (!isAuthenticated) {
      // No session — mint a guest one (once). When it succeeds,
      // isAuthenticated flips and this effect re-runs into the play path.
      if (guestAttemptedRef.current) return;
      guestAttemptedRef.current = true;

      const createGuest = async () => {
        const result = await startGuestSession(starterId);
        if (!result.success) {
          if (result.message === 'venue_budget_exhausted') {
            showVenueBusy();
            return;
          }
          setError(
            result.message === 'starter_offline'
              ? 'This experience is taking a short break — the venue has paused it. Please check back soon!'
              : result.status === 404
                ? 'This experience is no longer available.'
                : result.status === 429
                  ? 'Too many new sessions from this connection. Please wait a minute and try again.'
                  : 'Could not start your guide. Check your connection and tap retry.'
          );
        }
      };
      createGuest();
      return;
    }

    startedRef.current = true;

    const start = async (mode) => {
      try {
        const response = await exploreAPI.play(starterId, mode);
        const data = response.data.data;
        if (data.choiceRequired) {
          setResumeChoice({
            completed: data.objectivesCompleted,
            total: data.objectivesTotal,
          });
          setChoiceBusy(false);
          return;
        }
        navigate(`/projects/${data.projectId}/chats/${data.chatId}`, { replace: true });
      } catch (err) {
        console.error('[PlayPage] Failed to start play session:', err);
        startedRef.current = false;
        setChoiceBusy(false);
        setResumeChoice(null);

        // Operator paused the starter — a friendly notice, not a dead end
        // (printed QRs stay out in the world while a guide is offline).
        if (err?.response?.data?.detail === 'starter_offline') {
          setError(
            'This experience is taking a short break — the venue has paused it. Please check back soon!'
          );
          return;
        }

        // Venue over its monthly session budget (blocked band)
        if (err?.response?.data?.detail === 'venue_budget_exhausted') {
          showVenueBusy();
          return;
        }

        // Guest scoped to another venue scanning this venue's QR: mint a
        // fresh guest for THIS venue (once) — the old session is replaced,
        // and each venue's allowance stays its own.
        if (
          err?.response?.status === 403 &&
          user?.authProvider === 'guest' &&
          !rescopeAttemptedRef.current
        ) {
          rescopeAttemptedRef.current = true;
          const result = await startGuestSession(starterId);
          if (result.success) return; // auth state flips, effect re-runs into play
          if (result.message === 'venue_budget_exhausted') {
            showVenueBusy();
            return;
          }
        }

        setError(
          err?.response?.status === 404
            ? 'This experience is no longer available.'
            : 'Could not start your guide. Check your connection and tap retry.'
        );
      }
    };

    startFnRef.current = start;
    start();
  }, [isLoading, isAuthenticated, starterId, navigate, startGuestSession, user, showVenueBusy]);

  const chooseMode = (mode) => {
    if (choiceBusy) return;
    setChoiceBusy(true);
    startedRef.current = true;
    startFnRef.current?.(mode);
  };

  if (venueBusy) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mb-6">
          <span className="text-emerald-400 text-xl">✦</span>
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">
          Gerade ist hier richtig viel los!
        </h1>
        <p className="text-white/50 text-sm max-w-xs mb-6">
          Dieses Erlebnis macht eine kurze Verschnaufpause. Schau später nochmal
          vorbei
          {busyPartner
            ? ` — oder entdecke, was ${busyPartner.name} sonst noch für dich hat.`
            : '.'}
        </p>
        {busyPartner?.slug && (
          <Link
            to={`/p/${busyPartner.slug}`}
            className="px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
          >
            Mehr von {busyPartner.name} entdecken
          </Link>
        )}
      </div>
    );
  }

  if (resumeChoice) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mb-6">
          <span className="text-emerald-400 text-xl">⏳</span>
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">
          {de ? 'Du hast eine Tour angefangen' : 'You have a tour in progress'}
        </h1>
        <p className="text-white/50 text-sm max-w-xs mb-8">
          {de
            ? `${resumeChoice.completed} von ${resumeChoice.total} Schritten sind schon geschafft.`
            : `${resumeChoice.completed} of ${resumeChoice.total} steps already done.`}
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <button
            onClick={() => chooseMode('resume')}
            disabled={choiceBusy}
            className="px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
          >
            {de ? 'Weitermachen, wo du warst' : 'Continue where you left off'}
          </button>
          <button
            onClick={() => chooseMode('fresh')}
            disabled={choiceBusy}
            className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white/80 text-sm font-semibold transition-colors"
          >
            {de ? 'Von vorne beginnen' : 'Start over'}
          </button>
        </div>
        {choiceBusy && (
          <div className="w-6 h-6 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin mt-6" />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-8 text-center">
      {!error ? (
        <>
          <div className="w-12 h-12 rounded-full border-2 border-emerald-400/30 border-t-emerald-400 animate-spin mb-6" />
          <h1 className="text-white text-xl font-semibold mb-2">
            Your guide is getting ready…
          </h1>
          <p className="text-white/50 text-sm max-w-xs">
            One moment — we're setting up your live experience.
          </p>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-400/30 flex items-center justify-center mb-6">
            <span className="text-red-400 text-xl">!</span>
          </div>
          <h1 className="text-white text-xl font-semibold mb-2">
            Something went wrong
          </h1>
          <p className="text-white/50 text-sm max-w-xs mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null);
              startedRef.current = false;
              guestAttemptedRef.current = false;
              rescopeAttemptedRef.current = false;
              window.location.reload();
            }}
            className="px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
};

export default PlayPage;
